import type { Schema } from '@dealdesk/api-schema';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api, unwrap } from '@/shared/api/client';
import { authGate } from '@/shared/api';
import { sessionKeys } from '@/shared/auth';

import { authKeys } from './queries';

/**
 * MFA token between the sign-in steps. Kept in memory only, never in the URL or storage.
 * `enrol` is true when the user has no authenticator yet (status mfa_enrolment_required): the token is then the
 * enrolment ticket for POST /auth/mfa/enrol.
 */
let pendingMfa: { token: string; email: string; enrol: boolean } | null = null;
export const getPendingMfa = () => pendingMfa;
export const clearPendingMfa = () => {
  pendingMfa = null;
};

export function useLogin() {
  return useMutation({
    mutationFn: (body: Schema<'LoginRequest'>) => unwrap(api.POST('/auth/login', { body })),
    onSuccess: (res, vars) => {
      if ((res.status === 'mfa_required' || res.status === 'mfa_enrolment_required') && res.mfaToken) pendingMfa = { token: res.mfaToken, email: vars.email, enrol: res.status === 'mfa_enrolment_required' };
    },
  });
}

export function useVerifyMfa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schema<'MfaRequest'>) => unwrap(api.POST('/auth/mfa', { body })),
    onSuccess: (session) => {
      pendingMfa = null;
      qc.setQueryData(sessionKeys.all, session);
      authGate.enable();
    },
  });
}

/** Second factor with a one-time recovery code instead of the authenticator. */
export function useVerifyRecoveryCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schema<'MfaRecoveryRequest'>) => unwrap(api.POST('/auth/mfa/recovery', { body })),
    onSuccess: (session) => {
      pendingMfa = null;
      qc.setQueryData(sessionKeys.all, session);
      authGate.enable();
    },
  });
}

/** First sign-in without an authenticator: the secret and otpauth URI (shown once). */
export function useStartMfaEnrolment() {
  return useMutation({
    mutationFn: (mfaToken: string) => unwrap(api.POST('/auth/mfa/enrol', { body: { mfaToken } })),
  });
}

/** Confirms enrolment with the first code; returns one-time recovery codes and signs the user in. */
export function useConfirmMfaEnrolment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schema<'MfaEnrolmentConfirm'>) => unwrap(api.POST('/auth/mfa/enrol/confirm', { body })),
    onSuccess: (res) => {
      pendingMfa = null;
      // The session goes in after the user has seen the recovery codes (the page navigates on Continue).
      qc.setQueryData(sessionKeys.all, res.session);
      authGate.enable();
    },
  });
}

const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('dealdesk-auth') : null;

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/auth/logout')),
    onSettled: () => {
      authGate.disable();
      qc.clear();
      channel?.postMessage('logout');
    },
  });
}

/** Ends every session of the user (this browser included). */
export function useLogoutAll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/auth/logout-all')),
    onSettled: () => {
      authGate.disable();
      qc.clear();
      channel?.postMessage('logout');
    },
  });
}

/** "Forgotten password": always accepted (202) so the answer never reveals whether the account exists. */
export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (body: Schema<'PasswordResetRequest'>) => unwrap(api.POST('/auth/password-reset/request', { body })),
  });
}

/** Sets a new password from a reset or invitation link. Ends every session of the user; never signs in. */
export function useCompletePasswordReset() {
  return useMutation({
    mutationFn: (body: Schema<'PasswordResetComplete'>) => unwrap(api.POST('/auth/password-reset/complete', { body })),
  });
}

/** Ends one of the user's sessions. Ending the current one signs this browser out. */
export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (s: { id: string; current: boolean }) => unwrap(api.DELETE('/me/sessions/{sessionId}', { params: { path: { sessionId: s.id } } })),
    onSuccess: (_r, s) => {
      if (s.current) {
        authGate.disable();
        qc.clear();
        channel?.postMessage('logout');
        return;
      }
      void qc.invalidateQueries({ queryKey: authKeys.sessions() });
    },
  });
}

/** Other tabs sign out too. */
export function onRemoteLogout(handler: () => void) {
  if (!channel) return () => undefined;
  const fn = (e: MessageEvent) => e.data === 'logout' && handler();
  channel.addEventListener('message', fn);
  return () => channel.removeEventListener('message', fn);
}

/** Re-authentication inside the app (session expired): password + MFA in one go. */
export async function reauthenticate(email: string, password: string, code: string) {
  const login = await unwrap(api.POST('/auth/login', { body: { email, password } }));
  // Re-authentication never enrols an authenticator: a user without one signs in again from the login page.
  if (login.status !== 'mfa_required' || !login.mfaToken) return login.session ?? null;
  return unwrap(api.POST('/auth/mfa', { body: { mfaToken: login.mfaToken, code } }));
}
